import React, { useMemo, useState } from 'react';
import Modal from '../../components/common/Modal';
import ExamSubjectSetup from './ExamSubjectSetup';

const CLASSES_LIST = ['NURSERY', 'PP-1', 'PP-2', '1ST CLASS', '2ND CLASS', '3RD CLASS'];
const SECTIONS_LIST = ['A', 'B', 'C', 'D'];
const ACADEMIC_YEARS = ['2023-2024', '2024-2025', '2025-2026', '2026-2027'];
const EXAM_TYPES = [
    'FORMATIVE ASSESSMENT -1', 'FORMATIVE ASSESSMENT -2',
    'SUMMATIVE ASSESSMENT-1', 'SUMMATIVE ASSESSMENT-2',
    'QUARTERLY EXAM', 'HALF YEARLY', 'ANNUAL EXAM',
];

const initialExams = [
    {
        id: 1, srNo: 1, examName: 'FORMATIVE ASSESSMENT -1', academicYear: '2025-2026',
        classesSections: ['NURSERY-A', 'NURSERY-B', 'PP-1-A', 'PP-2-A', '1ST CLASS-A'],
        createdDate: '2025-07-25 11:37:05',
    },
    {
        id: 2, srNo: 2, examName: 'SUMMATIVE ASSESSMENT-1', academicYear: '2025-2026',
        classesSections: ['NURSERY-A', 'NURSERY-B', 'PP-1-A'],
        createdDate: '2024-11-25 09:44:50',
    },
];

const emptyForm = () => ({ academicYear: '', examName: '', classesSections: [] });

/* Chip — never conditionally adds/removes child nodes */
function Chip({ label, selected, onClick }) {
    return (
        <span
            onClick={onClick}
            style={{
                cursor: 'pointer',
                userSelect: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 600,
                padding: '6px 14px',
                transition: 'all 0.15s',
                border: selected ? '2px solid #0d6efd' : '2px solid #dee2e6',
                background: selected ? '#0d6efd' : '#fff',
                color: selected ? '#fff' : '#495057',
                boxShadow: selected ? '0 2px 6px rgba(13,110,253,.25)' : 'none',
            }}
        >
            {/* Always rendered — visibility toggled, never mounted/unmounted */}
            <span style={{ fontSize: 11, visibility: selected ? 'visible' : 'hidden', width: 12, display: 'inline-block' }}>✔</span>
            {label}
        </span>
    );
}

export default function ExaminationSetup() {
    const [exams, setExams] = useState(initialExams);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [selectedSections, setSelectedSections] = useState([]);
    const [search, setSearch] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(1);

    /* ── modal helpers ── */
    const resetPicker = () => { setSelectedClasses([]); setSelectedSections([]); };

    const openAddModal = () => {
        setForm(emptyForm());
        resetPicker();
        setEditingId(null);
        setShowModal(true);
    };

    const openEditModal = (exam) => {
        setForm({ academicYear: exam.academicYear || '', examName: exam.examName, classesSections: [...exam.classesSections] });
        resetPicker();
        setEditingId(exam.id);
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); resetPicker(); };

    /* ── picker toggles ── */
    const toggleClass = cls => setSelectedClasses(p => p.includes(cls) ? p.filter(c => c !== cls) : [...p, cls]);
    const toggleSection = sec => setSelectedSections(p => p.includes(sec) ? p.filter(s => s !== sec) : [...p, sec]);

    /* ── build + commit combos ── */
    const buildCombos = (classes, sections) =>
        classes.flatMap(cls => sections.map(sec => `${cls}-${sec}`));

    const addCombinations = () => {
        if (!selectedClasses.length || !selectedSections.length) return;
        const newCombos = buildCombos(selectedClasses, selectedSections);
        setForm(prev => ({
            ...prev,
            classesSections: Array.from(new Set([...prev.classesSections, ...newCombos])),
        }));
        resetPicker();
    };

    const removeCombo = combo =>
        setForm(prev => ({ ...prev, classesSections: prev.classesSections.filter(c => c !== combo) }));

    /* ── save: auto-commit pending picker ── */
    const handleSave = () => {
        if (!form.academicYear) { alert('Please select an Academic Year.'); return; }
        if (!form.examName.trim()) { alert('Please select an Exam.'); return; }

        let finalCombos = [...form.classesSections];
        if (selectedClasses.length && selectedSections.length) {
            finalCombos = Array.from(new Set([...finalCombos, ...buildCombos(selectedClasses, selectedSections)]));
        }
        if (finalCombos.length === 0) { alert('Please select at least one Class and Section.'); return; }

        if (editingId) {
            setExams(prev => prev.map(e =>
                e.id === editingId
                    ? { ...e, academicYear: form.academicYear, examName: form.examName, classesSections: finalCombos }
                    : e
            ));
        } else {
            const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
            const newSrNo = exams.length ? Math.max(...exams.map(e => e.srNo)) + 1 : 1;
            setExams(prev => [...prev, {
                id: Date.now(), srNo: newSrNo,
                academicYear: form.academicYear, examName: form.examName,
                classesSections: finalCombos, createdDate: now,
            }]);
        }
        closeModal();
    };

    const handleDelete = id => {
        if (window.confirm('Delete this examination?'))
            setExams(prev => prev.filter(e => e.id !== id));
    };

    /* ── search + pagination ── */
    const filteredExams = useMemo(() => {
        if (!search.trim()) return exams;
        const t = search.toLowerCase();
        return exams.filter(e =>
            e.examName.toLowerCase().includes(t) ||
            (e.academicYear || '').toLowerCase().includes(t) ||
            e.classesSections.join(' ').toLowerCase().includes(t)
        );
    }, [exams, search]);

    const totalPages = Math.max(1, Math.ceil(filteredExams.length / rowsPerPage));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * rowsPerPage;
    const pagedExams = filteredExams.slice(pageStart, pageStart + rowsPerPage);

    /* ── group combos by class ── */
    const groupedCombos = useMemo(() => {
        const map = {};
        form.classesSections.forEach(combo => {
            const idx = combo.lastIndexOf('-');
            const cls = combo.slice(0, idx);
            const sec = combo.slice(idx + 1);
            if (!map[cls]) map[cls] = [];
            map[cls].push({ combo, sec });
        });
        return map;
    }, [form.classesSections]);

    const canAdd = selectedClasses.length > 0 && selectedSections.length > 0;
    //   const hasAdded = Object.keys(groupedCombos).length > 0;

    return (
        <div className="">

            {/* ── Table panel ── */}
            <div className="card">
                <div className="card-header">
                    <h5>List of Examination</h5>
                    <button type="button" className="btn-add" onClick={openAddModal}>
                        + ADD Examination
                    </button>
                </div>

                <div className="card-body">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <select className="form-select form-select-sm" style={{ width: 80 }} value={rowsPerPage}
                                onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
                                {[10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
                            </select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <label className="small text-muted mb-0">Search:</label>
                            <input className="form-control form-control-sm" style={{ width: 220 }}
                                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table exam-table table-bordered mb-2">
                            <thead>
                                <tr>
                                    <th>Sr.No</th><th>Academic Year</th><th>Exam Name</th>
                                    <th>Classes &amp; Sections</th><th>Created Date</th><th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedExams.length === 0 && (
                                    <tr><td colSpan={6} className="text-center text-muted py-4">No records found</td></tr>
                                )}
                                {pagedExams.map(exam => (
                                    <tr key={exam.id}>
                                        <td>{exam.srNo}</td>
                                        <td>{exam.academicYear || '-'}</td>
                                        <td>{exam.examName}</td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {exam.classesSections.map(cs => (
                                                    <span key={cs} style={{ background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', borderRadius: 12, padding: '2px 8px', fontSize: 12 }}>
                                                        {cs.replace('-', ' ')}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td>{exam.createdDate}</td>
                                        <td>
                                            <button className="btn btn-success btn-sm icon-btn me-1" onClick={() => openEditModal(exam)}>✎</button>
                                            <button className="btn btn-danger btn-sm icon-btn" onClick={() => handleDelete(exam.id)}>🗑</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex flex-wrap justify-content-between align-items-center mt-2">
                        <div className="small text-muted">
                            Showing {filteredExams.length === 0 ? 0 : pageStart + 1} to {Math.min(pageStart + rowsPerPage, filteredExams.length)} of {filteredExams.length} entries
                        </div>
                        <nav>
                            <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}>«</button>
                                </li>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                                    <li key={n} className={`page-item ${n === safePage ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setPage(n)}>{n}</button>
                                    </li>
                                ))}
                                <li className={`page-item ${safePage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>»</button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>

            {/* ══════════ MODAL ══════════ */}
            <Modal show={showModal} onClose={closeModal}
                title={editingId ? 'Edit Examination' : 'Add Examination'} size="xl">

                <div className="row g-3">

                    {/* Academic Year */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Academic Year <span className="text-danger">*</span></label>
                        <select className="form-select" value={form.academicYear}
                            onChange={e => setForm({ ...form, academicYear: e.target.value })}>
                            <option value="">Select Academic Year</option>
                            {ACADEMIC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>

                    {/* Exam Name */}
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Select Exam <span className="text-danger">*</span></label>
                        <select className="form-select" value={form.examName}
                            onChange={e => setForm({ ...form, examName: e.target.value })}>
                            <option value="">Select Exam</option>
                            {EXAM_TYPES.map(ex => <option key={ex} value={ex}>{ex}</option>)}
                        </select>
                    </div>

                    {/* Classes & Sections to assin subject start */}
                    
                    <ExamSubjectSetup />
                    
                    {/* Classes & Sections to assin subject end */}



                    {/* <div className="col-12">
            <hr className="my-1" />
            <label className="form-label fw-semibold">
              Classes &amp; Sections <span className="text-danger">*</span>
            </label>

             
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>

              
              <div style={{ borderRight: '1px solid #dee2e6', padding: 14, background: '#f8f9fa' }}>
                <div className="small fw-semibold text-muted mb-2" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Classes
                </div>
                <div className=" flex-column gap-2">
                  {CLASSES_LIST.map(cls => (
                    <Chip
                      key={cls}
                      label={cls}
                      selected={selectedClasses.includes(cls)}
                      onClick={() => toggleClass(cls)}
                    />
                  ))}
                </div>
              </div>

               
              <div style={{ padding: 14, background: '#f8f9fa' }}>
                <div className="small fw-semibold text-muted mb-2" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Sections
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {SECTIONS_LIST.map(sec => (
                    <Chip
                      key={sec}
                      label={sec}
                      selected={selectedSections.includes(sec)}
                      onClick={() => toggleSection(sec)}
                    />
                  ))}
                </div>
              </div>
            </div>
                    </div>*/}

                </div>



                <div className="d-flex justify-content-end gap-2 border-top pt-3 mt-3">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                    <button type="button" className="btn btn-success" onClick={handleSave}>
                        {editingId ? 'Update' : 'Save'}
                    </button>
                </div>
            </Modal>
        </div>
    );
}